// Auth API functions for backend authentication

const getBaseUrl = () => {
  return process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';
};

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  telephone: string | null;
  emailVerified: boolean;
  customClaims?: {
    admin?: boolean;
    level?: string;
  };
}

export interface AuthTokens {
  idToken: string;
  refreshToken: string;
  expiresIn: string;
}

// Store tokens in memory (could also use localStorage with encryption)
let authTokens: AuthTokens | null = null;
let tokenExpiry: number | null = null;

export const setAuthTokens = (tokens: AuthTokens) => {
  authTokens = tokens;
  // Calculate expiry time (expiresIn is in seconds)
  tokenExpiry = Date.now() + parseInt(tokens.expiresIn) * 1000;
  // Store in localStorage for persistence
  if (typeof window !== 'undefined') {
    localStorage.setItem('auth_tokens', JSON.stringify(tokens));
    localStorage.setItem('token_expiry', tokenExpiry.toString());
  }
};

export const getAuthTokens = (): AuthTokens | null => {
  if (authTokens) {
    return authTokens;
  }
  // Try to load from localStorage
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('auth_tokens');
    const expiry = localStorage.getItem('token_expiry');
    if (stored && expiry) {
      const tokens = JSON.parse(stored);
      const expiryTime = parseInt(expiry);
      if (Date.now() < expiryTime) {
        authTokens = tokens;
        tokenExpiry = expiryTime;
        return tokens;
      } else {
        // Tokens expired, clear them
        clearAuthTokens();
      }
    }
  }
  return null;
};

export const clearAuthTokens = () => {
  authTokens = null;
  tokenExpiry = null;
  if (typeof window !== 'undefined') {
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('token_expiry');
  }
};

export const getIdToken = async (): Promise<string | null> => {
  const tokens = getAuthTokens();
  if (!tokens) {
    return null;
  }

  // Check if token is expired or about to expire (within 5 minutes)
  if (tokenExpiry && Date.now() >= tokenExpiry - 5 * 60 * 1000) {
    // Token expired or about to expire, try to refresh
    try {
      const refreshed = await refreshToken(tokens.refreshToken);
      if (refreshed) {
        return refreshed.idToken;
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
      clearAuthTokens();
      return null;
    }
  }

  return tokens.idToken;
};

export const refreshToken = async (
  refreshToken: string
): Promise<AuthTokens | null> => {
  try {
    const response = await fetch(`${getBaseUrl()}/api/auth/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();
    if (data.success && data.data) {
      const tokens: AuthTokens = {
        idToken: data.data.idToken,
        refreshToken: data.data.refreshToken || refreshToken,
        expiresIn: data.data.expiresIn,
      };
      setAuthTokens(tokens);
      return tokens;
    }
    return null;
  } catch (error) {
    console.error('Error refreshing token:', error);
    return null;
  }
};

export const signUp = async (
  email: string,
  password: string,
  displayName?: string,
  telephone?: string
): Promise<{ user: AuthUser; tokens: AuthTokens }> => {
  const response = await fetch(`${getBaseUrl()}/api/auth/signup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password, displayName, telephone }),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to sign up');
  }

  const tokens: AuthTokens = {
    idToken: data.data.idToken,
    refreshToken: data.data.refreshToken,
    expiresIn: data.data.expiresIn,
  };

  setAuthTokens(tokens);

  const user: AuthUser = {
    uid: data.data.uid,
    email: data.data.email,
    displayName: data.data.displayName,
    photoURL: null,
    telephone: data.data.telephone || null,
    emailVerified: false,
  };

  return { user, tokens };
};

export const signIn = async (
  email: string,
  password: string
): Promise<{ user: AuthUser; tokens: AuthTokens }> => {
  const response = await fetch(`${getBaseUrl()}/api/auth/signin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Invalid email or password');
  }

  const tokens: AuthTokens = {
    idToken: data.data.idToken,
    refreshToken: data.data.refreshToken,
    expiresIn: data.data.expiresIn,
  };

  setAuthTokens(tokens);

  const user: AuthUser = {
    uid: data.data.uid,
    email: data.data.email,
    displayName: data.data.displayName,
    photoURL: data.data.photoURL || null,
    telephone: data.data.telephone || null,
    emailVerified: false,
  };

  return { user, tokens };
};

export const signOut = async (): Promise<void> => {
  clearAuthTokens();
};

export const sendPasswordResetEmail = async (email: string): Promise<void> => {
  const response = await fetch(`${getBaseUrl()}/api/auth/password-reset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to send password reset email');
  }
};

export const getCurrentUser = async (): Promise<AuthUser | null> => {
  const token = await getIdToken();
  if (!token) {
    return null;
  }

  try {
    const response = await fetch(`${getBaseUrl()}/api/auth/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
    });

    // Check if response is actually JSON before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      // Network error or non-JSON response - don't treat as auth error
      console.warn('Non-JSON response from auth endpoint, might be network issue');
      return null;
    }

    const data = await response.json();

    // Check for authentication errors specifically
    if (response.status === 401 || response.status === 403) {
      // Actual authentication error - token is invalid
      throw new Error('AUTH_ERROR: Unauthorized');
    }

    if (!response.ok || !data.success) {
      // Other API errors - might be temporary
      const errorMsg = data.error || 'Failed to get user';
      throw new Error(`API_ERROR: ${errorMsg}`);
    }

    return data.data as AuthUser;
  } catch (error: any) {
    // Network errors (fetch failures) should not clear tokens
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.warn('Network error getting current user, keeping tokens:', error.message);
      return null;
    }
    
    // Only log actual errors, not network issues
    if (error?.message?.includes('AUTH_ERROR')) {
      console.error('Authentication error getting current user:', error);
    } else {
      console.warn('Error getting current user (non-auth):', error.message);
    }
    
    return null;
  }
};

export const updateProfile = async (
  displayName?: string,
  photoURL?: string,
  telephone?: string
): Promise<AuthUser> => {
  const token = await getIdToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const body: { displayName?: string; photoURL?: string; telephone?: string } = {};
  if (displayName !== undefined) body.displayName = displayName;
  if (photoURL !== undefined) body.photoURL = photoURL;
  if (telephone !== undefined) body.telephone = telephone;

  const response = await fetch(`${getBaseUrl()}/api/auth/update-profile`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'auth-token': token,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to update profile');
  }

  return data.data as AuthUser;
};

